import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react';

import { ACTIVITY_ICON_MAP } from '../../utils/activityIcons';
import {
  DEFAULT_ACTIVITY_TIMEZONE,
  formatWallClockHm,
  formatWallClockYmd,
  instantToHourFraction,
  isoToLocalHourFraction,
} from '../../utils/activityDateTime';

const HOUR_START = 8;
const HOUR_END = 22;
const ROW_HEIGHT_PX = 48;

export type ActivityCalendarEntry = {
  id: string;
  title: string;
  activity_type: string;
  due_at: string | null;
  end_at?: string | null;
  done?: boolean;
  primary_employee_id?: string | null;
  assignee_employees?: { id: string }[];
};

export type ActivityCalendarTypeOption = { id: string; label: string; icon: string };

export type ActivityCalendarPreviewSlot = {
  title: string;
  startTime: string;
  endTime: string;
  activityType?: string;
};

function parseTimeMinutes(time: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) return null;
  const h = Number.parseInt(m[1], 10);
  const min = Number.parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

function blockFromTimeRange(
  startTime: string,
  endTime: string,
  totalMinutes: number,
): { topPx: number; heightPx: number } | null {
  const startMin = parseTimeMinutes(startTime);
  let endMin = parseTimeMinutes(endTime);
  if (startMin == null) return null;
  if (endMin == null || endMin <= startMin) endMin = startMin + 60;
  const topMin = Math.max(0, startMin - HOUR_START * 60);
  const bottomMin = Math.min(totalMinutes, endMin - HOUR_START * 60);
  if (bottomMin <= topMin) return null;
  const topPx = (topMin / 60) * ROW_HEIGHT_PX;
  const heightPx = Math.max(ROW_HEIGHT_PX / 2, ((bottomMin - topMin) / 60) * ROW_HEIGHT_PX);
  return { topPx, heightPx };
}

function isValidDisplayDate(displayDate: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(displayDate);
}

export function filterActivitiesForEinsatzteam(
  activities: ActivityCalendarEntry[],
  assigneeEmployeeIds: string[],
): ActivityCalendarEntry[] {
  const open = activities.filter((a) => !a.done);
  if (assigneeEmployeeIds.length === 0) return open;
  const allowed = new Set(assigneeEmployeeIds);
  return open.filter(
    (a) =>
      (a.assignee_employees ?? []).some((e) => allowed.has(e.id)) ||
      (a.primary_employee_id != null && allowed.has(a.primary_employee_id)),
  );
}

/** Formular links, Kalender rechts – Höhe des Kalenders = Höhe der Formularspalte (Zeitstrahl scrollt intern). */
export function ActivityFormWithSideCalendar({
  formColumn,
  calendar,
  className = '',
  calendarFrom = 'md',
}: {
  formColumn: React.ReactNode;
  calendar: React.ReactNode;
  className?: string;
  /** Ab welchem Breakpoint der Kalender neben dem Formular erscheint. */
  calendarFrom?: 'sm' | 'md';
}) {
  const formColRef = React.useRef<HTMLDivElement>(null);
  const [sideHeight, setSideHeight] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const el = formColRef.current;
    if (!el) return;
    const measure = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      setSideHeight(Math.max(0, h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const calendarWrapClass =
    calendarFrom === 'sm' ? 'hidden w-72 shrink-0 sm:block' : 'hidden w-72 shrink-0 md:block';

  return (
    <div className={`flex h-full min-h-0 flex-1 items-stretch overflow-hidden ${className}`.trim()}>
      <div ref={formColRef} className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {formColumn}
      </div>
      <div
        className={`${calendarWrapClass} min-h-0 overflow-hidden`}
        style={
          sideHeight != null
            ? { height: sideHeight, maxHeight: '100%' }
            : { maxHeight: 0, overflow: 'hidden' }
        }
      >
        {calendar}
      </div>
    </div>
  );
}

export function ActivityDayCalendar({
  displayDate,
  onPrevDay,
  onNextDay,
  activities,
  activityTypes,
  previewSlot,
  unavailable = [],
  timeZone = DEFAULT_ACTIVITY_TIMEZONE,
}: {
  displayDate: string;
  onPrevDay: () => void;
  onNextDay: () => void;
  activities: ActivityCalendarEntry[];
  activityTypes: ActivityCalendarTypeOption[];
  /** Geplanter Termin aus dem Formular – wird gestrichelt im Zeitstrahl angezeigt. */
  previewSlot?: ActivityCalendarPreviewSlot | null;
  /** Einsatzteam members who are absent or already booked in the planned slot. */
  unavailable?: { id: string; name: string; hint: string }[];
  timeZone?: string;
}) {
  const safeDisplayDate = isValidDisplayDate(displayDate) ? displayDate : '';
  const dateObj = safeDisplayDate
    ? new Date(safeDisplayDate + 'T12:00:00')
    : new Date();
  const headerLabel = Number.isNaN(dateObj.getTime())
    ? 'Datum wählen'
    : dateObj.toLocaleDateString('de-DE', { weekday: 'long', month: 'long', day: 'numeric' });
  const today = new Date();
  const isToday = safeDisplayDate === formatWallClockYmd(today, timeZone);
  const nowMinutes = isToday
    ? Math.round(instantToHourFraction(today, timeZone) * 60)
    : null;
  const totalMinutes = (HOUR_END - HOUR_START) * 60;
  const totalHeight = (HOUR_END - HOUR_START) * ROW_HEIGHT_PX;

  const activitiesOnDay = activities.filter((a) => {
    if (!a.due_at) return false;
    return formatWallClockYmd(new Date(a.due_at), timeZone) === safeDisplayDate;
  });

  const blocks = activitiesOnDay
    .map((a) => {
      const start = a.due_at ? new Date(a.due_at) : null;
      let end = a.end_at ? new Date(a.end_at) : null;
      if (!start) return null;
      if (!end || end <= start) end = new Date(start.getTime() + 60 * 60 * 1000);
      const startMin = Math.round(isoToLocalHourFraction(start.toISOString(), timeZone) * 60);
      const endMin = Math.round(isoToLocalHourFraction(end.toISOString(), timeZone) * 60);
      const topMin = Math.max(0, startMin - HOUR_START * 60);
      const bottomMin = Math.min(totalMinutes, endMin - HOUR_START * 60);
      const topPx = (topMin / 60) * ROW_HEIGHT_PX;
      const heightPx = Math.max(ROW_HEIGHT_PX / 2, ((bottomMin - topMin) / 60) * ROW_HEIGHT_PX);
      const typeEntry = activityTypes.find((t) => t.id === a.activity_type);
      const IconComponent = typeEntry ? (ACTIVITY_ICON_MAP[typeEntry.icon] ?? CalendarClock) : CalendarClock;
      return { id: a.id, title: a.title, topPx, heightPx, Icon: IconComponent };
    })
    .filter(Boolean) as { id: string; title: string; topPx: number; heightPx: number; Icon: LucideIcon }[];

  const previewBlock = previewSlot
    ? blockFromTimeRange(previewSlot.startTime, previewSlot.endTime, totalMinutes)
    : null;
  const previewTypeEntry = previewSlot?.activityType
    ? activityTypes.find((t) => t.id === previewSlot.activityType)
    : null;
  const PreviewIcon = previewTypeEntry
    ? (ACTIVITY_ICON_MAP[previewTypeEntry.icon] ?? CalendarClock)
    : CalendarClock;

  const nowTopPx =
    isToday && nowMinutes != null ? ((nowMinutes - HOUR_START * 60) / 60) * ROW_HEIGHT_PX : null;

  const timelineScrollRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = timelineScrollRef.current;
    if (!el) return;
    const focusPx = previewBlock?.topPx ?? nowTopPx;
    if (focusPx == null) return;
    el.scrollTop = Math.max(0, focusPx - Math.round(el.clientHeight * 0.25));
  }, [safeDisplayDate, previewBlock?.topPx, previewBlock?.heightPx, nowTopPx, previewSlot?.startTime, previewSlot?.endTime]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-app-border bg-app-surface">
      <div className="flex shrink-0 items-center justify-between border-b border-app-border px-3 py-2">
        <span className="text-sm font-medium text-app-text">{headerLabel}</span>
        <div className="flex items-center">
          <button
            type="button"
            onClick={onPrevDay}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-app-text-muted hover:bg-app-hover hover:text-app-text"
            aria-label="Vorheriger Tag"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onNextDay}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-app-text-muted hover:bg-app-hover hover:text-app-text"
            aria-label="Nächster Tag"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      {unavailable.length > 0 ? (
        <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <div className="mb-0.5 flex items-center gap-1.5 font-semibold">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Nicht verfügbar
          </div>
          <ul className="space-y-0.5">
            {unavailable.map((u) => (
              <li key={u.id} className="break-words">
                <span className="font-medium">{u.name}:</span> {u.hint}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div ref={timelineScrollRef} className="app-scrollbar relative min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
        <div className="relative px-0.5" style={{ height: totalHeight }}>
          {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i).map((h) => (
            <div key={h} className="flex border-b border-app-border" style={{ height: ROW_HEIGHT_PX }}>
              <div className="w-10 shrink-0 pt-0.5 pr-2 text-right text-xs text-slate-400">{h}:00</div>
              <div className="flex-1 min-w-0" />
            </div>
          ))}
          {blocks.map((b) => (
            <div
              key={b.id}
              className="absolute left-10 right-2 mx-0.5 rounded bg-pipedrive-blue text-white overflow-hidden flex items-center gap-1.5 px-2 py-1 text-xs font-medium z-[5]"
              style={{ top: b.topPx + 2, height: b.heightPx - 4, minHeight: 28 }}
            >
              <b.Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{b.title}</span>
            </div>
          ))}
          {previewBlock && previewSlot ? (
            <div
              className={`absolute left-10 right-2 mx-0.5 z-[5] rounded border-2 border-dashed ${
                unavailable.length > 0 ? 'border-amber-500' : 'border-pipedrive-blue'
              } bg-sky-50/95 text-app-text overflow-hidden flex items-center gap-1.5 px-2 py-1 text-xs font-semibold shadow-sm`}
              style={{ top: previewBlock.topPx + 2, height: previewBlock.heightPx - 4, minHeight: 28 }}
              title="Geplanter Termin (Vorschau)"
            >
              <PreviewIcon className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{previewSlot.title.trim() || 'Neue Aktivität'}</span>
            </div>
          ) : null}
          {isToday && nowMinutes != null && (
            <div
              className="absolute left-0 right-0 flex items-center pointer-events-none z-20"
              style={{
                top: nowTopPx ?? 0,
              }}
            >
              <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 ml-1" />
              <div className="flex-1 h-0.5 bg-red-500" />
              <span className="text-[10px] font-medium text-red-600 ml-1 bg-app-surface px-1 shrink-0">
                {formatWallClockHm(today, timeZone)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
